import { useCallback, useEffect, useState } from 'react';
import type { Operaciones } from '../../api/operaciones';
import type { Calendario, Cierre, NuevaSemana, Reservable, SemanaTipo } from '../../api/ops/disponibilidad';
import type { DatosNegocio, Sede } from '../../api/ops/negocio';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Contexto } from '../../componentes/Contexto';
import { Deshacer } from '../../componentes/Deshacer';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Fecha } from '../../componentes/Fecha';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { Ventana } from '../../componentes/Ventana';
import { Desplegable } from '../../componentes/Desplegable';
import { rolesDe } from '../../logica/acceso';
import { estadoDeError } from '../../logica/estados';
import {
    anioDe,
    bloquesDeSemana,
    DIAS,
    diasCambiados,
    erroresDeBloques,
    fechaLarga,
    hayErrores,
    hoyEn,
    rangoDeTramo,
    semanaDeBloques,
    semanaDeTramos,
    tramosDeSemana,
    type Bloque,
    type Semana as DatosSemana,
} from '../../logica/horario';
import { useCabecera, type AccionCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import { COMUNIDADES_ES, etiquetaDe, PAISES, ZONAS, zonaDe } from '../negocio/logica';
import type { PropsPantalla } from '../tipos';
import { textoDeErrorApi, useCambios, type Cambio } from './cambios';
import { Excepciones } from './Excepciones';
import { Impacto } from './Impacto';
import { Semana } from './Semana';
import { AYUDA_HORARIO, palabrasDe, PERMISO_CONFIRMAR, PERMISO_GESTIONAR, PERMISO_NEGOCIO, PERMISO_VER, TEXTO_CONFLICTO_ZONA, tramoPorDefecto } from './textos';
import '../../componentes/apartado.css';
import '../negocio/negocio.css';
import './horario.css';

/**
 * Negocio › Horario (3.1, con 3.2 debajo y la ventana 3.4). De qué calendario es el horario («Todo el negocio» o una sede,
 * RV Contexto, solo con más de una sede) y su zona horaria siempre a la vista (`effective_timezone`, G255; se cambia aquí
 * con vista previa). La semana tipo por bloques se edita en borrador («Cambios sin guardar» con «Se aplica desde») y se
 * guarda entera con `createWeeklySchedule`, después de la vista previa (`cambios.ts`). Una sede que hereda enseña el
 * horario del negocio atenuado; «Horario propio» crea su calendario la primera vez (G248). «Copiar a otras sedes»
 * (`copyWeeklySchedule`) y «Anular la programada» (G250) pasan también por la vista previa.
 *
 * Editan Propietario y Administrador (`disponibilidad.horarios.gestionar` y `disponibilidad.calendario_negocio.gestionar`);
 * el Encargado y Recepción no llegan aquí (G245: sin la entrada `horario` no hay pantalla). Con el negocio en solo
 * lectura se ve todo, sin botones. En un negocio de citas solo cambian los textos.
 */
const NEGOCIO = 'negocio';

interface Datos {
    negocio: DatosNegocio;
    citas: boolean;
    sedes: Sede[];
    calNegocio: Calendario | null;
    calSedes: Record<string, Calendario>;
    semanas: Record<string, SemanaTipo[]>;
}

interface Borrador {
    hereda: boolean;
    bloques: Bloque[];
}

const vigenteDe = (s: readonly SemanaTipo[] | undefined) => (s ?? []).find((x) => x.status === 'current');
const programadaDe = (s: readonly SemanaTipo[] | undefined) =>
    (s ?? []).filter((x) => x.status === 'scheduled').sort((a, b) => (a.valid_from < b.valid_from ? -1 : 1))[0];

/** El mismo objeto sin esa clave. */
function sin<T>(o: Readonly<Record<string, T>>, clave: string): Record<string, T> {
    return Object.fromEntries(Object.entries(o).filter(([k]) => k !== clave));
}

async function semanasDe(ops: Operaciones, negocio: string, calendarId: string): Promise<SemanaTipo[]> {
    return (await ops.listWeeklySchedules(negocio, calendarId, { limit: 100 })).data;
}

async function cargar(ops: Operaciones, negocio: string): Promise<Datos> {
    const [n, bloques, sedes, cn, cs] = await Promise.all([
        ops.getBusinessConVersion(negocio),
        ops.listEntitlements(negocio).catch(() => null),
        ops.listLocations(negocio, { limit: 100 }),
        ops.listCalendars(negocio, { kind: 'business', limit: 100 }),
        ops.listCalendars(negocio, { kind: 'location', limit: 100 }),
    ]);
    const calNegocio = cn.data.find((c) => c.status === 'active') ?? null;
    const calSedes: Record<string, Calendario> = {};
    for (const c of cs.data) {
        if (c.location_id !== undefined && c.status === 'active') {
            calSedes[c.location_id] = c;
        }
    }
    const calendarios = [...(calNegocio === null ? [] : [calNegocio]), ...Object.values(calSedes)];
    const listas = await Promise.all(calendarios.map((c) => semanasDe(ops, negocio, c.id)));
    const semanas: Record<string, SemanaTipo[]> = {};
    calendarios.forEach((c, i) => {
        semanas[c.id] = listas[i] ?? [];
    });
    const codigos = (bloques?.entitlements ?? []).map((e) => e.product_code);
    return {
        negocio: n.datos,
        citas: codigos.includes('citas') && !codigos.includes('reservas'),
        sedes: sedes.data.filter((s) => s.status === 'active'),
        calNegocio,
        calSedes,
        semanas,
    };
}

export function Horario({ ops, negocio, membresia, nombreNegocio, soloLectura, movil, busqueda, navegar }: PropsPantalla) {
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const puedeVer = permisos.puede(PERMISO_VER);
    const editable = permisos.puede(PERMISO_GESTIONAR) && permisos.puede(PERMISO_NEGOCIO) && !soloLectura;
    const puedeConfirmar = permisos.puede(PERMISO_CONFIRMAR);

    const [datos, setDatos] = useState<Datos | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [sel, setSel] = useState<string>(NEGOCIO);
    const [borradores, setBorradores] = useState<Record<string, Borrador>>({});
    const [desde, setDesde] = useState<Record<string, string>>({});
    const [guardadas, setGuardadas] = useState<Record<string, Borrador>>({});
    const [programadasOcultas, setProgramadasOcultas] = useState<ReadonlySet<string>>(new Set());
    const [erroresApi, setErroresApi] = useState<Record<string, string>>({});
    const [reservables, setReservables] = useState<Reservable[]>([]);
    const [cierres, setCierres] = useState<Cierre[]>([]);
    const [ventana, setVentana] = useState<'copia' | 'zona' | null>(null);
    const [copia, setCopia] = useState<{ destinos: string[]; desde: string }>({ destinos: [], desde: '' });
    const [zonaNueva, setZonaNueva] = useState('');
    const [trabajando, setTrabajando] = useState(false);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (!puedeVer) {
            return;
        }
        let vivo = true;
        cargar(ops, negocio)
            .then((d) => {
                if (vivo) {
                    setDatos(d);
                    setError(null);
                    setGuardadas({});
                    setProgramadasOcultas(new Set());
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, intento, puedeVer]);

    // Nombres de zonas y recursos (cierres) y los cierres y retenes: solo consulta; si fallan, no salen.
    useEffect(() => {
        if (!puedeVer) {
            return;
        }
        let vivo = true;
        Promise.all([ops.listBookables(negocio, { limit: 100 }).catch(() => null), ops.listClosures(negocio, { limit: 25 }).catch(() => null)])
            .then(([b, c]) => {
                if (vivo) {
                    setReservables(b?.data ?? []);
                    setCierres(c?.data ?? []);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, puedeVer]);

    // `?apartado=excepciones` baja hasta «Excepciones y festivos».
    const bajar = busqueda.get('apartado') === 'excepciones';
    const cargado = datos !== null;
    useEffect(() => {
        if (!bajar || !cargado) {
            return;
        }
        const t = setTimeout(() => {
            try {
                document.getElementById('rv-excepciones')?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
            } catch {
                // Sin desplazamiento (navegador sin scrollIntoView): la sección sigue ahí debajo.
            }
        }, 300);
        return () => {
            clearTimeout(t);
        };
    }, [bajar, cargado]);

    const palabras = palabrasDe(datos?.negocio.vocabulary, datos?.citas ?? false);
    const c = useCambios({ ops, negocio, avisar, palabras });

    // ---- Lo que se ve del calendario elegido ----
    const d = datos;
    const esSede = sel !== NEGOCIO;
    const sede = d?.sedes.find((s) => s.id === sel);
    const calSede = esSede ? (d?.calSedes[sel] ?? null) : null;
    const calendario = esSede ? calSede : (d?.calNegocio ?? null);
    const semanasNegocio = d?.calNegocio === null || d === null ? [] : (d.semanas[d.calNegocio.id] ?? []);
    const semanasSel = calendario === null || d === null ? [] : (d.semanas[calendario.id] ?? []);
    const vigenteNegocio = vigenteDe(semanasNegocio) ?? programadaDe(semanasNegocio);
    const semanaNegocio: DatosSemana = semanaDeTramos(vigenteNegocio?.intervals ?? []);
    const vigente = vigenteDe(semanasSel);
    const programadaApi = programadaDe(semanasSel);
    const programada = programadaApi !== undefined && !programadasOcultas.has(programadaApi.id) ? programadaApi : undefined;
    const baseSel = vigente ?? programadaApi;
    const heredaApi = esSede && (calSede === null || baseSel === undefined || baseSel.inherits);
    const local = guardadas[sel];
    const hereda = local?.hereda ?? heredaApi;
    const semanaGuardada: DatosSemana = local !== undefined ? (local.hereda ? semanaNegocio : semanaDeBloques(local.bloques)) : hereda ? semanaNegocio : semanaDeTramos(baseSel?.intervals ?? []);
    const borrador = borradores[sel];
    const heredaVista = borrador?.hereda ?? hereda;
    const bloques = borrador !== undefined && !borrador.hereda ? borrador.bloques : bloquesDeSemana(heredaVista ? semanaNegocio : semanaGuardada);
    const vacia = !esSede && vigenteNegocio === undefined && local === undefined;
    const errores = erroresDeBloques(bloques).map((fila, bi) => fila.map((e, ti) => erroresApi[`${String(bi)}.${String(ti)}`] ?? e));
    const zona = calendario?.effective_timezone ?? d?.calNegocio?.effective_timezone ?? d?.negocio.timezone ?? 'Europe/Madrid';
    const hoy = hoyEn(zona);
    const anio = anioDe(hoy);
    const aplicaDesde = desde[sel] ?? hoy;
    const nombreSel = esSede ? (sede?.name ?? '') : 'todo el negocio';
    const porDefecto = tramoPorDefecto(palabras.citas);
    const editaSemana = editable && !heredaVista && (calendario !== null || borrador !== undefined);
    const sedesConPropio = (d?.sedes ?? []).filter((s) => {
        const cal = d?.calSedes[s.id];
        const base = cal === undefined ? undefined : (vigenteDe(d?.semanas[cal.id]) ?? programadaDe(d?.semanas[cal.id]));
        return base !== undefined && !base.inherits;
    });
    const otrasSedes = (d?.sedes ?? []).filter((s) => s.id !== sel);
    const puedeCopiar = editable && borrador === undefined && !heredaVista && !vacia && calendario !== null && otrasSedes.length > 0;

    // ---- Acciones ----
    const descartar = () => {
        setBorradores((b) => sin(b, sel));
        setErroresApi({});
    };
    const cambiarBloques = (nuevos: Bloque[]) => {
        setBorradores((b) => ({ ...b, [sel]: { hereda: false, bloques: nuevos } }));
        setErroresApi({});
        c.setHecho(null);
    };

    /** El calendario de la sede elegida; si aún no tiene, se crea (G248). */
    const asegurarCalendario = useCallback(
        async (locationId: string): Promise<Calendario | null> => {
            const actual = datos?.calSedes[locationId];
            if (actual !== undefined) {
                return actual;
            }
            try {
                const nuevo = (await ops.createCalendar(negocio, { location_id: locationId })).datos;
                setDatos((x) => (x === null ? x : { ...x, calSedes: { ...x.calSedes, [locationId]: nuevo }, semanas: { ...x.semanas, [nuevo.id]: [] } }));
                return nuevo;
            } catch (e) {
                avisar({ tipo: 'error', texto: textoDeErrorApi(e, 'No he podido preparar el horario de la sede. Prueba de nuevo.') });
                return null;
            }
        },
        [datos, ops, negocio, avisar],
    );
    const asegurarElegido = useCallback(async () => (sel === NEGOCIO ? (datos?.calNegocio ?? null) : asegurarCalendario(sel)), [sel, datos, asegurarCalendario]);

    const horarioPropio = async (si: boolean) => {
        if (!si) {
            if (heredaApi && local === undefined) {
                descartar();
            } else {
                setBorradores((b) => ({ ...b, [sel]: { hereda: true, bloques: [] } }));
            }
            return;
        }
        setTrabajando(true);
        const cal = await asegurarCalendario(sel);
        setTrabajando(false);
        if (cal === null) {
            return;
        }
        if (!heredaApi && local === undefined) {
            descartar();
            return;
        }
        setBorradores((b) => ({ ...b, [sel]: { hereda: false, bloques: bloquesDeSemana(semanaNegocio) } }));
    };

    const guardar = async () => {
        if (borrador === undefined || calendario === null) {
            return;
        }
        if (!borrador.hereda && hayErrores(errores)) {
            return;
        }
        const calId = calendario.id;
        const clave = sel;
        const fecha = aplicaDesde;
        const semana: NuevaSemana = borrador.hereda ? { valid_from: fecha, inherits: true } : { valid_from: fecha, inherits: false, intervals: tramosDeSemana(semanaDeBloques(borrador.bloques)) };
        // Para poner cada 422 bajo su tramo: de qué bloque y tramo sale cada tramo que se manda.
        const origen = borrador.hereda
            ? []
            : (semana.intervals ?? []).map((t) => {
                  const bi = borrador.bloques.findIndex((b) => b.dias.includes(t.weekday - 1));
                  const r = rangoDeTramo(t);
                  const ti = (borrador.bloques[bi]?.tramos ?? []).findIndex((x) => x.desde === r.desde && x.hasta === r.hasta);
                  return `${String(bi)}.${String(ti)}`;
              });
        const guardado = borrador;
        const cambio: Cambio = {
            contexto: `Semana tipo de ${nombreSel} · desde el ${fechaLarga(fecha, anio)}`,
            icono: 'clock',
            todoElNegocio: !esSede,
            peticion: { kind: 'weekly_schedule', calendar_id: calId, weekly_schedule: semana },
            aplicar: (changeId, k) => ops.createWeeklySchedule(negocio, calId, { ...semana, ...(changeId === undefined ? {} : { change_id: changeId }) }, k).then(() => undefined),
            textoDeshacer: `Horario guardado. Se aplica desde ${fecha === hoy ? 'hoy' : `el ${fechaLarga(fecha, anio)}`}.`,
            alProgramar: () => {
                setGuardadas((g) => ({ ...g, [clave]: guardado }));
                setBorradores((b) => sin(b, clave));
            },
            alDeshacer: () => {
                setGuardadas((g) => sin(g, clave));
                setBorradores((b) => ({ ...b, [clave]: guardado }));
            },
            alGuardar: () => {
                setBorradores((b) => sin(b, clave));
                recargar();
            },
            alFallar: (e) => {
                if (!(e instanceof ErrorApi) || e.codigo !== 'validation_failed') {
                    return false;
                }
                const r: Record<string, string> = {};
                for (const x of e.errores) {
                    const m = /intervals(?:\.|\[)(\d+)/.exec(x.field);
                    const donde = m === null ? undefined : origen[Number(m[1])];
                    if (donde !== undefined) {
                        r[donde] = x.message ?? 'Revisa este tramo.';
                    }
                }
                if (Object.keys(r).length === 0) {
                    return false;
                }
                setErroresApi(r);
                return true;
            },
        };
        await c.pedir(cambio);
    };

    const anularProgramada = async () => {
        if (programada === undefined || calendario === null) {
            return;
        }
        const calId = calendario.id;
        const p = programada;
        await c.pedir({
            contexto: `Anular el horario programado · desde el ${fechaLarga(p.valid_from, anio)}`,
            icono: 'calendar-x',
            todoElNegocio: !esSede,
            peticion: { kind: 'withdraw_schedule', calendar_id: calId, schedule_id: p.id },
            aplicar: (changeId, k) => ops.withdrawWeeklySchedule(negocio, calId, p.id, changeId, k),
            textoDeshacer: 'Horario programado anulado. Sigue el vigente.',
            alProgramar: () => {
                setProgramadasOcultas((o) => new Set([...o, p.id]));
            },
            alDeshacer: () => {
                setProgramadasOcultas((o) => new Set([...o].filter((x) => x !== p.id)));
            },
            alGuardar: recargar,
        });
    };

    const abrirCopia = () => {
        setCopia({ destinos: [], desde: hoy });
        setVentana('copia');
    };
    const copiar = async () => {
        if (calendario === null || copia.destinos.length === 0 || d === null) {
            return;
        }
        setTrabajando(true);
        const destinos: Calendario[] = [];
        for (const id of copia.destinos) {
            const cal = await asegurarCalendario(id);
            if (cal === null) {
                setTrabajando(false);
                return;
            }
            destinos.push(cal);
        }
        setTrabajando(false);
        setVentana(null);
        const nombres = copia.destinos.map((id) => d.sedes.find((s) => s.id === id)?.name ?? '').filter((n) => n !== '');
        const lista = nombres.length < 2 ? nombres.join('') : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1] ?? ''}`;
        const datosCopia = { target_calendar_ids: destinos.map((x) => x.id), valid_from: copia.desde };
        const origen = calendario.id;
        await c.pedir({
            contexto: `Copiar el horario a ${lista} · desde el ${fechaLarga(copia.desde, anio)}`,
            icono: 'copy',
            todoElNegocio: false,
            peticion: { kind: 'copy', calendar_id: origen, copy: datosCopia },
            aplicar: (changeId, k) => ops.copyWeeklySchedule(negocio, origen, { ...datosCopia, ...(changeId === undefined ? {} : { change_id: changeId }) }, k).then(() => undefined),
            textoDeshacer: `Horario copiado a ${lista}.`,
            alGuardar: recargar,
        });
    };

    const abrirZona = () => {
        setZonaNueva(esSede ? (calSede?.timezone ?? '') : (d?.calNegocio?.timezone ?? zona));
        setVentana('zona');
    };
    const zonaActual = esSede ? (calSede?.timezone ?? '') : (d?.calNegocio?.timezone ?? zona);
    const cambiarZona = async () => {
        if (d === null) {
            return;
        }
        setTrabajando(true);
        const cal = await asegurarElegido();
        if (cal === null) {
            setTrabajando(false);
            return;
        }
        let version: string | null;
        try {
            version = (await ops.getCalendar(negocio, cal.id)).version;
        } catch (e) {
            setTrabajando(false);
            avisar({ tipo: 'error', texto: textoDeErrorApi(e, 'No he podido leer el horario. Prueba de nuevo.') });
            return;
        }
        setTrabajando(false);
        setVentana(null);
        const nueva = zonaNueva === '' ? null : zonaNueva;
        const destino = nueva ?? d.calNegocio?.effective_timezone ?? d.negocio.timezone;
        const calId = cal.id;
        await c.pedir({
            contexto: `Zona horaria de ${esSede ? (sede?.name ?? '') : 'todo el negocio'} → ${nueva === null ? `la del negocio (${zonaDe(destino).toLocaleLowerCase('es')})` : zonaDe(nueva)}`,
            icono: 'globe',
            zona: true,
            todoElNegocio: !esSede,
            peticion: { kind: 'timezone', calendar_id: calId, timezone: destino },
            aplicar: (changeId, k) => ops.updateCalendar(negocio, calId, { timezone: nueva, ...(changeId === undefined ? {} : { change_id: changeId }) }, version ?? '', k).then(() => undefined),
            textoDeshacer: 'Zona horaria cambiada.',
            alGuardar: recargar,
            alFallar: (e) => {
                if (e instanceof ErrorApi && e.codigo === 'version_conflict') {
                    avisar({ tipo: 'error', texto: TEXTO_CONFLICTO_ZONA });
                    recargar();
                    return true;
                }
                return false;
            },
        });
    };

    // ---- Cabecera: «?» y el botón del momento ----
    const acciones: AccionCabecera[] = [];
    if (editable && borrador !== undefined) {
        acciones.push({ clave: 'descartar', texto: 'Descartar', icono: 'x', secundaria: true, onClick: descartar });
        acciones.push({
            clave: 'guardar',
            texto: c.calculando ? 'Calculando…' : 'Guardar horario',
            icono: 'check',
            desactivado: c.calculando,
            onClick: () => {
                void guardar();
            },
        });
    } else if (puedeCopiar && !movil) {
        acciones.push({ clave: 'copiar', texto: 'Copiar a otras sedes', icono: 'copy', secundaria: true, onClick: abrirCopia });
    }
    useCabecera({ ayuda: puedeVer ? AYUDA_HORARIO(palabras.citas) : [], acciones });

    if (!puedeVer) {
        return <EstadoSinPermiso seccion="el horario del negocio" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    if (error !== null && d === null) {
        return <EstadoError seccion="el horario" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (d === null) {
        return <EstadoCargando seccion="el horario" />;
    }

    // ---- Textos de la vista ----
    const cambiados = borrador === undefined || borrador.hereda ? [] : diasCambiados(semanaGuardada, semanaDeBloques(borrador.bloques));
    const textoCambios =
        borrador !== undefined && esSede && borrador.hereda !== hereda
            ? borrador.hereda
                ? 'Volverá a usar el horario de todo el negocio.'
                : 'Tendrá su propia semana.'
            : cambiados.length === 0
              ? 'Sin diferencias con el horario guardado.'
              : cambiados.length === 1
                ? `Has cambiado el ${DIAS[cambiados[0] ?? 0] ?? ''}.`
                : `Has cambiado ${String(cambiados.length)} días: ${cambiados.map((i) => DIAS[i] ?? '').join(', ').replace(/, ([^,]*)$/, ' y $1')}.`;
    const textoVersion = vacia
        ? 'Todavía no hay ninguna semana guardada.'
        : esSede && hereda
          ? vigente === undefined
              ? 'Usa el horario de todo el negocio.'
              : `Usa el horario de todo el negocio desde el ${fechaLarga(vigente.valid_from, anio)}.`
          : vigente === undefined
            ? programada === undefined
                ? 'Todavía no hay ninguna semana guardada.'
                : `Hay una semana programada desde el ${fechaLarga(programada.valid_from, anio)}.`
            : `Vigente desde el ${fechaLarga(vigente.valid_from, anio)}.${programada === undefined ? '' : ` Hay otra programada desde el ${fechaLarga(programada.valid_from, anio)}.`}`;
    const nota = soloLectura
        ? 'El negocio está en solo lectura: puedes consultar el horario, pero no cambiarlo.'
        : !editable
          ? 'Puedes consultar el horario. Lo cambian el propietario y los administradores.'
          : null;
    const opcionesCalendario = [{ valor: NEGOCIO, texto: 'Todo el negocio' }, ...d.sedes.map((s) => ({ valor: s.id, texto: s.name }))];
    const regionSede = esSede ? (sede?.region_code ?? null) : null;
    // «Todo el negocio» (G372): la comunidad solo si todas las sedes activas la tienen y es la misma; si no (comunidades
    // distintas, alguna sin comunidad o ninguna sede), solo los nacionales.
    const regionesNegocio = [...new Set(d.sedes.map((s) => (s.region_code == null || s.region_code === '' ? null : s.region_code)))];
    const regionComun = d.sedes.length > 0 && regionesNegocio.length === 1 ? (regionesNegocio[0] ?? null) : null;
    const region = esSede ? (regionSede === '' ? null : regionSede) : regionComun;
    const opcionesZona = [...(esSede ? [{ valor: '', texto: `La del negocio (${zonaDe(d.calNegocio?.effective_timezone ?? d.negocio.timezone)})` }] : []), ...Object.entries(ZONAS).map(([valor, texto]) => ({ valor, texto: `${texto} · ${valor}` }))];

    return (
        <div className="rv-hor">
            {c.envio.pendiente !== null && <Deshacer texto={c.envio.pendiente.texto} onDeshacer={c.envio.deshacer} clave={c.envio.pendiente.clave} duracion={c.envio.duracion} movil={movil} />}
            {c.hecho !== null && (
                <div role="status" className="rv-ca-hecho">
                    <span className="rv-ca-hecho__baldosa">
                        <Icono nombre="check" />
                    </span>
                    <span>{c.hecho}</span>
                </div>
            )}
            {nota !== null && (
                <p className="rv-neg-nota">
                    <Icono nombre="eye" />
                    <span>{nota}</span>
                </p>
            )}
            <div className="rv-hor-barra">
                {d.sedes.length > 1 && (
                    <div className="rv-hor-barra__contexto">
                        <Contexto
                            etiqueta="Horario de"
                            valor={sel}
                            opciones={opcionesCalendario}
                            icono={esSede ? 'map-pin' : 'store'}
                            movil={movil}
                            onChange={(v) => {
                                setSel(v);
                                setErroresApi({});
                                c.setHecho(null);
                            }}
                        />
                    </div>
                )}
                <div className="rv-hor-zona">
                    <Icono nombre="globe" />
                    <span className="rv-hor-zona__texto">
                        <strong>{movil ? zonaDe(zona) : `${zonaDe(zona)} · ${zona}`}</strong>
                        {!movil && <span>{esSede && calSede?.timezone != null ? 'Zona propia de esta sede' : 'La del negocio'}</span>}
                    </span>
                    {editable && (
                        <Boton tipo="secundario" icono="pencil" onClick={abrirZona} aria-label="Cambiar la zona horaria">
                            Cambiar
                        </Boton>
                    )}
                </div>
                {puedeCopiar && movil && (
                    <Boton tipo="secundario" icono="copy" onClick={abrirCopia}>
                        Copiar a otras sedes
                    </Boton>
                )}
            </div>

            {esSede && (
                <div className="rv-hor-tarjeta">
                    <Interruptor
                        etiqueta="Horario propio"
                        ayuda={heredaVista ? 'Ahora usa el horario de todo el negocio.' : 'Esta sede tiene su propia semana.'}
                        valor={!heredaVista}
                        desactivado={!editable || trabajando}
                        linea="ninguna"
                        onChange={(v) => {
                            void horarioPropio(v);
                        }}
                    />
                </div>
            )}
            {vacia && (
                <div role="status" className="rv-hor-vacia">
                    <Icono nombre="triangle-alert" />
                    <span>
                        <strong>Todavía no has puesto tu horario</strong>
                        <span>Mientras todos los días estén cerrados, nadie puede reservar. Elige los días que abres y a qué hora.</span>
                    </span>
                </div>
            )}
            {borrador !== undefined && (
                <div className="rv-hor-cambios">
                    <div className="rv-hor-cambios__texto">
                        <span className="rv-hor-cambios__baldosa">
                            <Icono nombre="pencil" />
                        </span>
                        <span>
                            <strong>Cambios sin guardar</strong>
                            <span>{textoCambios}</span>
                        </span>
                    </div>
                    <div className="rv-hor-cambios__fecha">
                        <Fecha
                            etiqueta="Se aplica desde"
                            valor={aplicaDesde}
                            min={hoy}
                            ayuda="Hasta ese día sigue el horario de ahora."
                            bloqueado={!editable}
                            onChange={(v) => {
                                if (v !== '') {
                                    setDesde((x) => ({ ...x, [sel]: v < hoy ? hoy : v }));
                                }
                            }}
                        />
                    </div>
                </div>
            )}

            <Semana bloques={bloques} errores={errores} editable={editaSemana} atenuado={heredaVista && esSede} porDefecto={porDefecto} movil={movil} ocultarCerrados={vacia && borrador === undefined} onCambiar={cambiarBloques} />

            <div className="rv-hor-version">
                <span>
                    <Icono nombre="history" />
                    <span>{textoVersion}</span>
                </span>
                {editable && programada !== undefined && !(esSede && hereda) && (
                    <Boton
                        tipo="secundario"
                        rojo
                        icono="x"
                        onClick={() => {
                            void anularProgramada();
                        }}
                    >
                        Anular la programada
                    </Boton>
                )}
            </div>

            <Excepciones
                ops={ops}
                negocio={negocio}
                calendario={calendario}
                calNegocio={d.calNegocio}
                esSede={esSede}
                nombreSede={sede?.name ?? ''}
                sedeConPropio={!hereda}
                sedesConPropio={sedesConPropio.map((s) => s.name)}
                pais={d.negocio.country}
                nombrePais={etiquetaDe(PAISES, d.negocio.country)}
                region={region}
                comunidadesMezcladas={d.sedes.length > 1 && regionesNegocio.length > 1}
                nombreRegion={region === null ? null : etiquetaDe(COMUNIDADES_ES, region)}
                zona={zona}
                hoy={hoy}
                editable={editable}
                semana={semanaGuardada}
                porDefecto={porDefecto}
                movil={movil}
                reservables={reservables}
                cierres={cierres}
                calculando={c.calculando}
                pedir={c.pedir}
                asegurarCalendario={asegurarElegido}
                avisar={avisar}
            />

            <Impacto
                impacto={c.impacto}
                palabras={palabras}
                anio={anio}
                movil={movil}
                puedeConfirmar={puedeConfirmar}
                onConfirmar={() => {
                    void c.confirmar();
                }}
                onCerrar={c.cerrarImpacto}
            />

            <Ventana
                abierta={ventana === 'copia'}
                titulo="Copiar el horario a otras sedes"
                texto={`Las sedes elegidas tendrán la semana de ${nombreSel} como horario propio. No toca sus días especiales.`}
                accion="Copiar"
                accionIcono="copy"
                inactiva={copia.destinos.length === 0}
                ocupado={trabajando}
                ocupadoTexto="Preparando…"
                movil={movil}
                onAccion={() => {
                    void copiar();
                }}
                onCerrar={() => {
                    setVentana(null);
                }}
            >
                <div className="rv-hor-copia">
                    {otrasSedes.map((s) => {
                        const cal = d.calSedes[s.id];
                        const base = cal === undefined ? undefined : (vigenteDe(d.semanas[cal.id]) ?? programadaDe(d.semanas[cal.id]));
                        const propio = base !== undefined && !base.inherits;
                        const elegida = copia.destinos.includes(s.id);
                        return (
                            <Interruptor
                                key={s.id}
                                etiqueta={s.name}
                                ayuda={propio ? 'Ahora tiene horario propio' : 'Ahora usa el del negocio'}
                                valor={elegida}
                                onChange={() => {
                                    setCopia((x) => ({ ...x, destinos: elegida ? x.destinos.filter((y) => y !== s.id) : [...x.destinos, s.id] }));
                                }}
                            />
                        );
                    })}
                    <Fecha
                        etiqueta="Desde"
                        valor={copia.desde}
                        min={hoy}
                        onChange={(v) => {
                            if (v !== '') {
                                setCopia((x) => ({ ...x, desde: v }));
                            }
                        }}
                    />
                </div>
            </Ventana>

            <Ventana
                abierta={ventana === 'zona'}
                titulo={`Zona horaria de ${esSede ? (sede?.name ?? '') : nombreNegocio}`}
                texto={esSede ? 'Todas las horas de esta sede se leen en esta zona: el horario, los días especiales y las reservas.' : 'Todas las horas del negocio se leen en esta zona: el horario, los días especiales y las reservas.'}
                accion="Cambiar la zona"
                accionIcono="globe"
                inactiva={zonaNueva === zonaActual}
                ocupado={trabajando}
                ocupadoTexto="Preparando…"
                movil={movil}
                onAccion={() => {
                    void cambiarZona();
                }}
                onCerrar={() => {
                    setVentana(null);
                }}
            >
                <Desplegable
                    etiqueta="Zona horaria"
                    valor={zonaNueva}
                    opciones={opcionesZona}
                    onChange={setZonaNueva}
                />
            </Ventana>
        </div>
    );
}
